import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';
import { supabase, tables } from '@/lib/supabase';

export interface ActiveStudentNotEnrolled {
  id: string;
  first_name: string;
  last_name: string;
  family_name: string;
  last_enrollment_date: string;
  suggested_classes: string[];
  engagement_score: number;
}

export interface ActiveStudentsNotEnrolledData {
  students: ActiveStudentNotEnrolled[];
  count: number;
}

export const useActiveStudentsNotEnrolled = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useActiveStudentsNotEnrolled');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['activeStudentsNotEnrolled', organizationId] as const,
    queryFn: async (): Promise<ActiveStudentsNotEnrolledData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchActiveStudentsNotEnrolled',
          { userId: user?.id },
          false
        );
      }

      try {
        // Get students with good attendance (>80%) who have room for more classes
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        const twoMonthsAgoStr = twoMonthsAgo.toISOString().split('T')[0];

        // Get all students with their current enrollments and families
        const { data: studentsWithEnrollments, error: studentsError } = await tables
          .students()
          .select(`
            id,
            first_name,
            last_name,
            family_id,
            families!inner(name),
            enrollments!left(
              id,
              status,
              created_at
            )
          `)
          .eq('organization_id', organizationId);

        if (studentsError) throw studentsError;

        if (!studentsWithEnrollments || studentsWithEnrollments.length === 0) {
          return { students: [], count: 0 };
        }

        const expansionOpportunities: ActiveStudentNotEnrolled[] = [];

        for (const student of studentsWithEnrollments) {
          const activeEnrollments = student.enrollments?.filter(e => e.status === 'active') || [];
          
          // Look for students with 1-2 active enrollments (room for more)
          if (activeEnrollments.length >= 3 || activeEnrollments.length === 0) continue;

          // Get attendance data for this student
          const { data: attendanceRecords, error: attendanceError } = await tables
            .class_attendance()
            .select('status, date')
            .eq('organization_id', organizationId)
            .eq('student_id', student.id)
            .gte('date', twoMonthsAgoStr);

          if (attendanceError) continue;

          // Calculate engagement score (attendance percentage)
          const totalClasses = attendanceRecords?.length || 0;
          if (totalClasses === 0) continue;

          const attendedClasses = attendanceRecords?.filter(
            record => record.status === 'present' || record.status === 'late'
          ).length || 0;

          const engagementScore = Math.round((attendedClasses / totalClasses) * 100);

          // Only include students with good engagement (>80%)
          if (engagementScore >= 80) {
            const lastEnrollment = activeEnrollments.sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];

            expansionOpportunities.push({
              id: student.id,
              first_name: student.first_name || '',
              last_name: student.last_name || '',
              family_name: student.families?.name || 'Unknown Family',
              last_enrollment_date: lastEnrollment?.created_at || '',
              suggested_classes: ['Additional Classes'], // Would need logic to suggest specific classes
              engagement_score: engagementScore,
            });
          }
        }

        // Sort by engagement score (highest first)
        expansionOpportunities.sort((a, b) => b.engagement_score - a.engagement_score);

        return {
          students: expansionOpportunities,
          count: expansionOpportunities.length,
        };
      } catch (error) {
        throw errorHandler.handleError(
          error,
          'fetchActiveStudentsNotEnrolled',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false
        );
      }
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });
};